"use client";

import clsx from "clsx";
import {
  Button,
  Dropdown,
  Select,
  TextInput,
  ToggleSwitch,
} from "flowbite-react";
import { useMemo } from "react";
import {
  useForm,
  FormProvider,
  useFormContext,
  useFieldArray,
  useWatch,
  Controller,
} from "react-hook-form";
import { MdAdd, MdDelete } from "react-icons/md";

export interface IField {
  key: string;
  type: "textarea" | "select" | "number" | "list" | "checkbox";
  label: string;
  subfields?: IField[];
  options?: Array<{
    key: string;
    label: string;
  }>;
  cols?: number;
  validation?: Record<string, any>;
}

function WatcherFormArrayField({
  subfields,
  fieldKey: parentKey,
  label,
}: IField & { fieldKey: string }) {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const {
    fields: arrayFields = [{ id: "empty" }],
    append,
    prepend,
    remove,
    swap,
    move,
    insert,
  } = useFieldArray({
    name: parentKey,
    control,
  });
  return (
    <div>
      <div className="flex flex-row mb-2 items-center justify-between">
        <div>{label}</div>
        <div>
          <Button
            color="sky"
            onClick={() => {
              append({});
            }}
          >
            <MdAdd />
          </Button>
        </div>
      </div>

      {arrayFields.map((arrayField, index) => (
        <div key={arrayField.id} className="w-full flex flex-row gap-2">
          <div className="grow grid grid-cols-2 gap-2 col-span-3">
            {(subfields || []).map((field) => (
              <WatcherFormField
                {...field}
                key={field.key}
                fieldKey={`${parentKey}.${index}.${field.key}`}
              />
            ))}
          </div>
          <div className="flex flex-row items-end">
            <Button
              color="sky"
              onClick={() => {
                remove(index);
              }}
            >
              <MdDelete />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function WatcherFormFieldSelect({
  options,
  fieldKey,
}: {
  options: Array<{
    key: string;
    label: string;
  }>;
  fieldKey: string;
}) {
  const { setValue } = useFormContext();
  const key = useWatch({
    name: fieldKey,
  });
  const label = useMemo(() => {
    return options.find((option) => option.key === key)?.label;
  }, [options, key]);

  return (
    <Dropdown label={label} inline>
      {options.map((option) => (
        <Dropdown.Item
          key={option.key}
          onClick={() => {
            setValue(fieldKey, option.key);
          }}
        >
          {option.label}
        </Dropdown.Item>
      ))}
    </Dropdown>
  );
}

export function WatcherFormField({
  type,
  subfields = [],
  fieldKey,
  label,
  options = [],
  className = "",
  cols = 2,
  validation,
}: IField & { fieldKey: string; className?: string }) {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext();
  return (
    <div className={`col-span-${cols} ${className}`}>
      {type !== "list" && <div className="mb-2">{label}</div>}
      {type === "textarea" && <TextInput {...register(fieldKey, validation)} />}
      {type === "number" && <TextInput type="number" {...register(fieldKey, validation)} />}
      {type === "select" && (
        <WatcherFormFieldSelect options={options} fieldKey={fieldKey} />
      )}
      {type === "checkbox" && (
        <Controller
          name={fieldKey}
          control={control}
          render={({ field }) => (
            <ToggleSwitch checked={field.value} onChange={field.onChange} />
          )}
        />
      )}
      {type === "list" && (
        <WatcherFormArrayField
          key={fieldKey}
          type="list"
          label={label}
          fieldKey={fieldKey}
          subfields={subfields}
        />
      )}
      {errors[fieldKey] && <span className="text-rose-500">This Field Got Error</span>}
    </div>
  );
}

export default function WatcherForm({
  fields,
  defaultValues = {},
  onSubmit: onSubmitFromProps,
  formId,
  hideSubmit = false,
}: {
  fields: IField[];
  defaultValues: any;
  onSubmit: (data: any) => void;
  formId?: string;
  hideSubmit?: boolean;
}) {
  const methods = useForm({
    values: defaultValues,
  });
  const {
    handleSubmit,
    formState: { isDirty },
  } = methods;
  const onSubmit = (data: any) => {
    onSubmitFromProps(data);
  };

  return (
    <FormProvider {...methods}>
      <form
        id={formId}
        className="grid grid-cols-2 gap-2"
        onSubmit={handleSubmit(onSubmit)}
      >
        {fields.map((field) => (
          <WatcherFormField {...field} key={field.key} fieldKey={field.key} />
        ))}
        {!hideSubmit && (
          <Button
            color="sky"
            className="mt-4"
            disabled={!isDirty}
            type="submit"
          >
            Save
          </Button>
        )}
      </form>
    </FormProvider>
  );
}
